import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    CardContent,
    Divider,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useAlert } from '../../core/AlertContext';
import { RsvpStatus } from '../../Interfaces/IEventInteraction';
import {
    IParticipantDraft,
    IRegistrationSelections,
} from '../../Interfaces/IEventRegistration';
import { useEventRsvps, useSetEventRsvp } from '../../hooks/eventInteractions';
import { useEventRegistrationForm } from '../../hooks/eventRegistration';
import { parseBackendError } from '../backendError';
import { isRegistrationComplete, validateRegistrationDraft } from '../registrationValidation';
import { primaryButtonSx } from '../../core/pvtaStyles';
import EventGuestPicker from './EventGuestPicker';
import EventRegistrationAnswerForm from './EventRegistrationAnswerForm';
import EventRsvpTabs from './EventRsvpTabs';

interface EventRsvpSectionProps {
    eventId: number;
}

const RSVP_LABELS: Record<RsvpStatus, string> = {
    yes: 'Zusage',
    no: 'Absage',
    maybe: 'Vielleicht',
};

const emptyMember = (): IParticipantDraft => ({
    kind: 'MEMBER',
    name: '',
    comment: '',
    selections: {},
});

const EventRsvpSection: React.FC<EventRsvpSectionProps> = ({ eventId }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { showAlert } = useAlert();
    const { rsvps, isLoadingEventRsvps, isErrorEventRsvps } = useEventRsvps(eventId);
    const { registrationForm, isLoadingRegistrationForm } = useEventRegistrationForm(eventId);
    const setEventRsvpMutation = useSetEventRsvp();

    const currentUserId = useMemo(() => {
        const value = localStorage.getItem('user_id');
        if (!value) {
            return null;
        }
        const parsed = Number(value);
        return Number.isNaN(parsed) ? null : parsed;
    }, []);

    const ownRsvp = useMemo(
        () => (currentUserId ? rsvps.find((rsvp) => rsvp.user.id === currentUserId) : undefined),
        [currentUserId, rsvps]
    );

    const serverDraft = useMemo<IParticipantDraft[]>(() => {
        const participants = ownRsvp?.participants ?? [];
        if (participants.length === 0) {
            return [emptyMember()];
        }
        return participants.map((participant) => ({
            kind: participant.kind,
            name: participant.name,
            comment: participant.comment,
            selections: participant.selections,
        }));
    }, [ownRsvp]);

    const [draft, setDraft] = useState<IParticipantDraft[]>(serverDraft);
    const [hasLocalChanges, setHasLocalChanges] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<RsvpStatus | null>(ownRsvp?.status ?? null);

    // Serverzustand spiegeln, solange nichts Ungespeichertes offen ist.
    const serverSnapshot = JSON.stringify(serverDraft);
    useEffect(() => {
        if (!hasLocalChanges) {
            setDraft(JSON.parse(serverSnapshot) as IParticipantDraft[]);
        }
    }, [serverSnapshot, hasLocalChanges]);

    useEffect(() => {
        if (!hasLocalChanges) {
            setSelectedStatus(ownRsvp?.status ?? null);
        }
    }, [ownRsvp?.status, hasLocalChanges]);

    const hasFields = (registrationForm?.fields.length ?? 0) > 0;
    const commentEnabled = registrationForm?.comment_enabled ?? false;
    const hasRegistration = hasFields || commentEnabled;

    const guestNames = useMemo(
        () => draft.filter((participant) => participant.kind === 'GUEST').map((participant) => participant.name),
        [draft]
    );

    const validationResults = useMemo(
        () => (selectedStatus === 'yes' ? validateRegistrationDraft(registrationForm, draft) : []),
        [draft, registrationForm, selectedStatus]
    );

    const submit = useCallback(
        async (status: RsvpStatus, participants: IParticipantDraft[]): Promise<void> => {
            if (!currentUserId) {
                showAlert({ severity: 'warning', text: 'Bitte zuerst einloggen.' });
                return;
            }
            try {
                await setEventRsvpMutation.mutateAsync({ eventId, status, participants });
                // Entwurf erst nach erfolgreichem Speichern festschreiben. Vorher
                // zurueckzusetzen verliert bei einem Fehlschlag die Eingaben, und der
                // Server-Spiegel-Effekt zieht den Entwurf auf den alten Stand, bevor
                // der Refetch da ist.
                setDraft(participants);
                setHasLocalChanges(false);
                showAlert({ severity: 'success', text: `Anmeldung gespeichert: ${RSVP_LABELS[status]}` });
            } catch (error) {
                showAlert({ severity: 'error', text: parseBackendError(error).message });
            }
        },
        [currentUserId, eventId, setEventRsvpMutation, showAlert]
    );

    /**
     * Entscheidung D12: ohne Zusatzanmeldung bleibt das Sofortspeichern erhalten
     * (Abnahmekriterium E). Nur "Ich komme" mit Zusatzanmeldung braucht den
     * expliziten Speicherschritt, weil erst dann Pflichtangaben vorliegen koennen.
     */
    const handleStatusClick = async (status: RsvpStatus): Promise<void> => {
        setSelectedStatus(status);

        if (status === 'no') {
            // Absage ist eindeutig -> sofort speichern. Der Entwurf wird erst im
            // Erfolgsfall in submit() zurueckgesetzt.
            await submit('no', [emptyMember()]);
            return;
        }

        // Sofortspeichern nur, wenn es nichts auszufuellen und nichts Ungespeichertes
        // gibt (Entscheidung D12 / Abnahmekriterium E). Sonst setzt der Klick bloss den
        // Status, und "Anmeldung speichern" schickt den vollstaendigen Entwurf.
        if (hasRegistration || hasLocalChanges) {
            setHasLocalChanges(true);
            return;
        }

        await submit(status, draft);
    };

    const handleGuestCountChange = (nextCount: number): void => {
        setHasLocalChanges(true);
        setDraft((previous) => {
            const member = previous.find((participant) => participant.kind === 'MEMBER') ?? emptyMember();
            const guests = previous.filter((participant) => participant.kind === 'GUEST');
            const nextGuests = [...guests];
            while (nextGuests.length > nextCount) {
                nextGuests.pop();
            }
            while (nextGuests.length < nextCount) {
                nextGuests.push({ kind: 'GUEST', name: '', comment: '', selections: {} });
            }
            return [member, ...nextGuests];
        });
    };

    const handleGuestNameChange = (guestIndex: number, name: string): void => {
        setHasLocalChanges(true);
        setDraft((previous) => previous.map((participant, index) => (
            index === guestIndex + 1 ? { ...participant, name } : participant
        )));
    };

    const handleSelectionsChange = (
        participantIndex: number,
        selections: IRegistrationSelections
    ): void => {
        setHasLocalChanges(true);
        setDraft((previous) => previous.map((participant, index) => (
            index === participantIndex ? { ...participant, selections } : participant
        )));
    };

    const handleCommentChange = (participantIndex: number, comment: string): void => {
        setHasLocalChanges(true);
        setDraft((previous) => previous.map((participant, index) => (
            index === participantIndex ? { ...participant, comment } : participant
        )));
    };

    const showParticipantPanel = selectedStatus === 'yes' || selectedStatus === 'maybe';
    const showSaveButton = showParticipantPanel && (hasRegistration || hasLocalChanges);
    const saveDisabled = setEventRsvpMutation.isPending
        || (selectedStatus === 'yes' && !isRegistrationComplete(registrationForm, draft));

    return (
        <Card variant="outlined" sx={{ borderRadius: 2.5, height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Teilnahme
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {hasRegistration
                        ? 'Für diese Veranstaltung sind zusätzliche Angaben nötig.'
                        : 'Dein Status wird sofort aktualisiert.'}
                </Typography>

                <Stack direction={isMobile ? 'column' : 'row'} spacing={1} sx={{ mb: 2 }}>
                    <Button
                        color="success"
                        variant={selectedStatus === 'yes' ? 'contained' : 'outlined'}
                        onClick={() => void handleStatusClick('yes')}
                        disabled={setEventRsvpMutation.isPending || isLoadingEventRsvps || isLoadingRegistrationForm}
                        fullWidth
                        sx={{ minHeight: 44 }}
                    >
                        Zusage
                    </Button>
                    <Button
                        color="error"
                        variant={selectedStatus === 'no' ? 'contained' : 'outlined'}
                        onClick={() => void handleStatusClick('no')}
                        disabled={setEventRsvpMutation.isPending || isLoadingEventRsvps || isLoadingRegistrationForm}
                        fullWidth
                        sx={{ minHeight: 44 }}
                    >
                        Absage
                    </Button>
                    <Button
                        color="warning"
                        variant={selectedStatus === 'maybe' ? 'contained' : 'outlined'}
                        onClick={() => void handleStatusClick('maybe')}
                        disabled={setEventRsvpMutation.isPending || isLoadingEventRsvps || isLoadingRegistrationForm}
                        fullWidth
                        sx={{ minHeight: 44 }}
                    >
                        Vielleicht
                    </Button>
                </Stack>

                {isErrorEventRsvps && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                        RSVP-Daten konnten nicht geladen werden.
                    </Alert>
                )}

                {showParticipantPanel && (
                    <Stack spacing={2} sx={{ mb: 2 }}>
                        <EventGuestPicker
                            guestNames={guestNames}
                            onChangeGuestCount={handleGuestCountChange}
                            onChangeGuestName={handleGuestNameChange}
                            disabled={setEventRsvpMutation.isPending}
                        />

                        {hasRegistration && registrationForm && (
                            <>
                                <Divider />
                                {draft.map((participant, participantIndex) => (
                                    <EventRegistrationAnswerForm
                                        key={`participant-${participantIndex}`}
                                        form={registrationForm}
                                        participant={participant}
                                        participantIndex={participantIndex}
                                        onChangeSelections={handleSelectionsChange}
                                        onChangeComment={handleCommentChange}
                                        disabled={setEventRsvpMutation.isPending}
                                        required={selectedStatus === 'yes'}
                                    />
                                ))}
                            </>
                        )}

                        {validationResults.length > 0 && (
                            <Alert severity="info">
                                Noch offen:{' '}
                                {validationResults
                                    .map((result) => `${result.label} (${result.missingFieldLabels.join(', ')})`)
                                    .join(' · ')}
                            </Alert>
                        )}

                        {showSaveButton && (
                            <Button
                                onClick={() => void submit(selectedStatus as RsvpStatus, draft)}
                                disabled={saveDisabled}
                                sx={{ ...primaryButtonSx, minHeight: 44 }}
                            >
                                Anmeldung speichern
                            </Button>
                        )}
                    </Stack>
                )}

                <EventRsvpTabs rsvps={rsvps} />
            </CardContent>
        </Card>
    );
};

export default EventRsvpSection;
